import { gsap } from "gsap";
import { Signal } from "typed-signals";
import { LayoutController } from "onearm";

/**
 * @class BetSettingsController
 * @description Controller for bet settings panel with multiple bet panels
 */
export class BetSettingsController extends LayoutController {
    init() {
        this.currencyFormatter = this.options.currencyFormatter;

        this.onPressPlus = new Signal();
        this.onPressMinus = new Signal();
        this.onPressBetPlus = new Signal();
        this.onPressBetMinus = new Signal();
        this.onPressCoinValuePlus = new Signal();
        this.onPressCoinValueMinus = new Signal();
        this.onPressTotalBetPlus = new Signal();
        this.onPressTotalBetMinus = new Signal();
        this.onPressBetMax = new Signal();
        this.onPressClose = new Signal();

        this.setupMainBetButtons();
        this.setupBetSettingsPanel();
        this.setupBetButton();
    }

    setupMainBetButtons() {
        this.forAll("SpinControlls.PlusButton", (btn) => {
            btn.onPress.connect(() => this.onPressPlus.emit());
        });
        this.forAll("SpinControlls.MinusButton", (btn) => {
            btn.onPress.connect(() => this.onPressMinus.emit());
        });
        this.forAll("BetControlls.BetPlusButton", (btn) => {
            btn.onPress.connect(() => this.onPressTotalBetPlus.emit());
        });
        this.forAll("BetControlls.BetMinusButton", (btn) => {
            btn.onPress.connect(() => this.onPressTotalBetMinus.emit());
        });
        this.forAll("BetPanel.PlusButton", (btn) => {
            btn.onPress.connect(() => this.onPressBetPlus.emit());
        });
        this.forAll("BetPanel.MinusButton", (btn) => {
            btn.onPress.connect(() => this.onPressBetMinus.emit());
        });
        this.forAll("CoinPanel.PlusButton", (btn) => {
            btn.onPress.connect(() => this.onPressCoinValuePlus.emit());
        });
        this.forAll("CoinPanel.MinusButton", (btn) => {
            btn.onPress.connect(() => this.onPressCoinValueMinus.emit());
        });
        this.forAll("TotalBetPanel.PlusButton", (btn) => {
            btn.onPress.connect(() => this.onPressTotalBetPlus.emit());
        });
        this.forAll("TotalBetPanel.MinusButton", (btn) => {
            btn.onPress.connect(() => this.onPressTotalBetMinus.emit());
        });
    }

    setupBetSettingsPanel() {
        this.forAll("BetSettings", (betSettings) => {
            betSettings.visible = false;
            betSettings.alpha = 0;
        });

        this.forAll("BetSettings.BetMaxButton", (btn) => {
            btn.onPress.connect(() => this.onPressBetMax.emit());
        });

        this.forAll("BetSettings.CloseButton", (btn) => {
            btn.onPress.connect(() => this.onPressClose.emit());
        });
        this.forAll("BetSettings.OkButton", (btn) => {
            btn.onPress.connect(() => this.onPressClose.emit());
        });
    }

    setupBetButton() {
        this.forAll("BetButton", (btn) => {
            btn.onPress.connect(() => this.toggle());
        });
    }

    formatCurrency(value) {
        return this.currencyFormatter?.format(value) ?? String(value);
    }

    toggle() {
        const betSettings = this.get("BetSettings");

        if (betSettings) {
            if (betSettings.visible) {
                this.hideBetSettings();
            } else {
                this.showBetSettings();
            }
        }
    }

    setVisibleBetsPopup(visible) {
        const betInfoPopup = this.get("BetInfoPopup");
        const betSettings = this.get("BetSettings");

        if (betSettings && !betInfoPopup) {
            if (betSettings.visible && !visible) {
                this.hideBetSettings();
            } else if (!betSettings.visible && visible) {
                this.showBetSettings();
            }
        } else if (betInfoPopup && visible) {
            this.showBetInfoPopup();
        }
    }

    showBetInfoPopup() {
        this.forAll("BetInfoPopup", (betInfoPopup) => {
            gsap.killTweensOf(betInfoPopup);

            const tl = gsap.timeline();
            tl.set(betInfoPopup, {visible: true});
            tl.playSfx("common_popup_windows");
            tl.to(betInfoPopup, {
                alpha: 1,
                duration: 0.25,
                ease: "power2.inOut",
            })
            tl.to(betInfoPopup, {
                alpha: 0,
                duration: 0.25,
                ease: "power2.inOut",
            }, 1);
            tl.set(betInfoPopup, {
                visible: false,
                alpha: 0,
            });
        });
    }

    showBetSettings() {
        this.forAll("BetSettings", (betSettings) => {
            betSettings.visible = true;
            gsap.killTweensOf(betSettings);

            const tl = gsap.timeline();
            tl.playSfx("common_popup_windows");
            tl.to(betSettings, {
                alpha: 1,
                duration: 0.25,
                ease: "power2.inOut",
            });
        });
    }

    hideBetSettings() {
        this.forAll("BetSettings", (betSettings) => {
            gsap.killTweensOf(betSettings);

            const tl = gsap.timeline();
            tl.playSfx("hide_popup");
            tl.to(betSettings, {
                alpha: 0,
                duration: 0.25,
                ease: "power2.inOut",
                onComplete: () => {
                    betSettings.visible = false;
                },
            });
        });
    }

    isVisible() {
        return this.forAll("BetSettings", (betSettings) => betSettings.visible);
    }

    /**
     * @param {number} value - Bet value (1-10)
     */
    setBetValue(value) {
        this.forAll("BetPanel.BetsValueContainer.BetsValueText", (text) => {
            text.text = this.formatCurrency(value);
        });
    }

    /**
     * @param {number} value - Coin value (0.01-0.50)
     */
    setCoinValue(value) {
        this.forAll("CoinPanel.BetsValueContainer.BetsValueText", (text) => {
            text.text = this.formatCurrency(value);
        });
    }

    /**
     * @param {number} value - Total bet value
     */
    setTotalBetValue(value) {
        this.forAll("TotalBetPanel.BetsValueContainer.BetsValueText", (text) => {
            text.text = this.formatCurrency(value);
        });
    }
}

